<template>
  <div class="p-0">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Mi Horario</h1>
      <button
        @click="refreshSchedule"
        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
        :disabled="loading"
      >
        <i class="fas fa-sync-alt mr-2" :class="{ 'animate-spin': loading }"></i>
        Actualizar
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <span class="ml-3 text-gray-600">Cargando horario...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error al cargar horario</h3>
          <p class="text-sm text-red-700 mt-1">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Desktop Table View -->
    <div v-if="!loading && scheduleData.length > 0" class="hidden lg:block">
      <div class="bg-white rounded-lg shadow-md overflow-hidden">
        <div class="table-responsive">
          <table class="min-w-full">
            <thead>
              <tr>
                <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-100 uppercase border-b border-gray-200 bg-indigo-800">
                  Horario
                </th>
                <th v-for="day in days" :key="day.id" scope="col" class="px-3 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-100 uppercase border-b border-gray-200 bg-indigo-800">
                  {{ day.name }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <tr v-for="(row, index) in rowsSchedules" :key="index">
                <!-- Time Column -->
                <td class="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 bg-gray-50">
                  {{ row.time }}
                </td>

                <!-- Day Columns -->
                <td v-for="day in days" :key="day.id" class="px-3 py-2 whitespace-nowrap align-top">
                  <div v-if="getScheduleForDayAndRow(day.id, index)" class="py-2 px-4 border border-gray-300 bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
                    <!-- Subject, Class, Shift and Teacher Info -->
                    <div class="text-xs">
                      <div class="font-semibold text-blue-800 mb-1">
                        {{ getScheduleForDayAndRow(day.id, index).class_subject.subject.name }}
                      </div>
                      <div class="text-blue-600 mb-1">
                        <span class="font-medium">Curso:</span>
                        {{ getScheduleForDayAndRow(day.id, index).class_subject.class.course_description }} {{ getScheduleForDayAndRow(day.id, index).class_subject.class.division_description }}
                      </div>
                      <div class="text-blue-600 mb-1">
                        <span class="font-medium">Turno:</span>
                        {{ getScheduleForDayAndRow(day.id, index).shift.name }}
                      </div>
                      <div class="text-blue-600">
                        <span class="font-medium">Profesor:</span>
                        {{ getTeacherName(getScheduleForDayAndRow(day.id, index).user_id) }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="py-2 px-4 border border-gray-200 bg-gray-50 text-gray-400 text-xs text-center">
                    Libre
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Mobile Card View -->
    <div v-if="!loading && scheduleData.length > 0" class="lg:hidden">
      <div class="space-y-4">
        <div v-for="day in days" :key="day.id" class="bg-white rounded-lg shadow-md border border-gray-200">
          <div class="px-4 py-3 bg-indigo-800 text-white font-semibold">
            {{ day.name }}
          </div>
          <div class="p-4">
            <div v-if="getDaySchedules(day.id).length > 0" class="space-y-3">
              <div
                v-for="schedule in getDaySchedules(day.id)"
                :key="schedule.id"
                class="bg-blue-50 border border-blue-200 rounded-lg p-3"
              >
                <div class="flex justify-between items-start mb-2">
                  <h4 class="font-semibold text-blue-800">
                    {{ schedule.class_subject.subject.name }}
                  </h4>
                  <span class="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                    {{ schedule.schedule.description }}
                  </span>
                </div>
                <div class="text-sm text-blue-700 space-y-1">
                  <div>
                    <span class="font-medium">Curso:</span>
                    {{ schedule.class_subject.class.course_description }} {{ schedule.class_subject.class.division_description }}
                  </div>
                  <div>
                    <span class="font-medium">Turno:</span>
                    {{ schedule.shift.name }}
                  </div>
                  <div>
                    <span class="font-medium">Profesor:</span>
                    {{ getTeacherName(schedule.user_id) }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-gray-500 py-4">
              Sin clases programadas
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !error && scheduleData.length === 0" class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No tienes horario asignado</h3>
      <p class="mt-1 text-sm text-gray-500">Tu horario aparecerá aquí una vez que sea asignado.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "MySchedules",
  data() {
    return {
      scheduleData: [],
      days: [],
      teachers: [],
      rowsSchedules: [],
      loading: true,
      error: null,
    };
  },
  mounted() {
    this.getSchedule();
    this.getDays();
    this.getTeachers();
  },
  methods: {
    getSchedule() {
      this.loading = true;
      this.error = null;

      this.$http.get('/my-schedule')
        .then(response => {
          const data = response.data;
          if (data.status === 'success') {
            this.scheduleData = data.data || [];
            this.processScheduleData();
          } else {
            this.error = data.message || 'Error al obtener el horario';
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching schedule:', error);
          this.error = error.message || 'Error al cargar el horario';
          this.scheduleData = [];
          this.loading = false;
        });
    },
    getDays() {
      this.$http.get('/days').then(response => {
        this.days = response.data.data || response.data || [];
      }).catch(err => console.log('Error getting days:', err));
    },
    getTeachers() {
      this.$http.get('/roles').then(res => {
        const role_teacher_id = res.data.find(r => r.name === 'teacher')?.id;
        if (role_teacher_id) {
          this.$http.get('/user-by-role-id/' + role_teacher_id).then(response => {
            this.teachers = response.data.data || response.data || [];
          }).catch(err => console.log('Error getting teachers:', err));
        }
      }).catch(err => console.log('Error getting roles:', err));
    },
    processScheduleData() {
      if (this.scheduleData.length === 0) return;

      // Create rows based on unique schedule descriptions, sorted by time_start
      const uniqueTimes = [...new Set(this.scheduleData.map(s => s.schedule.description))].sort((a, b) => {
        const aTime = this.scheduleData.find(s => s.schedule.description === a).schedule.time_start;
        const bTime = this.scheduleData.find(s => s.schedule.description === b).schedule.time_start;
        return aTime.localeCompare(bTime);
      });
      this.rowsSchedules = uniqueTimes.map(time => ({ time }));
    },
    getScheduleForDayAndRow(dayId, rowIndex) {
      const time = this.rowsSchedules[rowIndex]?.time;
      return this.scheduleData.find(schedule => schedule.day.id === dayId && schedule.schedule.description === time);
    },
    getDaySchedules(dayId) {
      return this.scheduleData.filter(schedule => schedule.day.id === dayId).sort((a, b) => a.schedule.time_start.localeCompare(b.schedule.time_start));
    },
    getTeacherName(userId) {
      const teacher = this.teachers.find(t => t.User?.id === userId);
      if (teacher && teacher.Profile) {
        return `${teacher.Profile.lastname}, ${teacher.Profile.firstname}`;
      }
      return 'Sin asignar';
    },
    refreshSchedule() {
      this.getSchedule();
    }
  }
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

@media (max-width: 1024px) {
  .table-responsive {
    display: none;
  }
}
</style>